import React, { useEffect, useState } from 'react';
import { Drawer, Box, IconButton, Divider, Switch, FormControlLabel } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Typography, Button, Input } from '@/components/ui';

export interface UserFormValues {
  id?: string;
  name: string;
  email: string;
  phone_number: string;
  password?: string;
  is_active: boolean;
}

export interface FormErrors {
  name?: string;
  email?: string;
  phone_number?: string;
  password?: string;
}

export interface UserFormDrawerProps {
  open: boolean;
  initialValues?: UserFormValues | null;
  onClose: () => void;
  onSubmit: (values: UserFormValues) => void;
}

const drawerWidth = 480;

// Validation helper functions
const validateName = (name: string): string | undefined => {
  if (!name.trim()) {
    return 'Name is required';
  }
  if (name.trim().length > 50) {
    return 'Name must not exceed 50 characters';
  }
  return undefined;
};

const validateEmail = (email: string): string | undefined => {
  if (!email.trim()) {
    return 'Email is required';
  }
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email.trim())) {
    return 'Please enter a valid email address';
  }
  return undefined;
};

const validatePhoneNumber = (phone: string): string | undefined => {
  if (!phone.trim()) {
    return undefined; // Phone is optional
  }
  const phoneRegex = /^\d{10}$/;
  if (!phoneRegex.test(phone.trim())) {
    return 'Phone number must be exactly 10 digits';
  }
  return undefined;
};

const validatePassword = (password: string, isEdit: boolean): string | undefined => {
  if (!isEdit && !password.trim()) {
    return 'Password is required';
  }
  return undefined;
};

const UserFormDrawer: React.FC<UserFormDrawerProps> = ({ open, initialValues, onClose, onSubmit }) => {
  const [values, setValues] = useState<UserFormValues>({
    name: '',
    email: '',
    phone_number: '',
    password: '',
    is_active: true,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const isEdit = Boolean(initialValues?.id);

  useEffect(() => {
    if (initialValues) {
      setValues({
        id: initialValues.id,
        name: initialValues.name || '',
        email: initialValues.email || '',
        phone_number: initialValues.phone_number || '',
        password: '',
        is_active: initialValues.is_active ?? true,
      });
    } else {
      setValues({
        name: '',
        email: '',
        phone_number: '',
        password: '',
        is_active: true,
      });
    }
    // Clear errors when form resets
    setErrors({});
  }, [initialValues]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setValues(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    // Real-time validation
    let error: string | undefined;
    switch (name) {
      case 'name':
        error = validateName(value);
        break;
      case 'email':
        error = validateEmail(value);
        break;
      case 'phone_number':
        error = validatePhoneNumber(value);
        break;
      case 'password':
        error = validatePassword(value, isEdit);
        break;
      default:
        break;
    }

    setErrors(prev => ({
      ...prev,
      [name]: error,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate all fields
    const nameError = validateName(values.name);
    const emailError = validateEmail(values.email);
    const phoneError = validatePhoneNumber(values.phone_number);
    const passwordError = validatePassword(values.password || '', isEdit);
    
    const newErrors: FormErrors = {
      name: nameError,
      email: emailError,
      phone_number: phoneError,
      password: passwordError,
    };
    
    setErrors(newErrors);
    
    // Check if there are any errors
    const hasErrors = Object.values(newErrors).some(error => error !== undefined);
    
    if (hasErrors) {
      return; // Don't submit if there are validation errors
    }
    
    onSubmit({
      ...values,
      name: values.name.trim(),
      email: values.email.trim(),
      phone_number: values.phone_number.trim(),
      password: values.password?.trim(),
    });
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  return (
    <Drawer anchor="right" open={open} onClose={handleClose} PaperProps={{ sx: { width: drawerWidth } }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" px={2} py={2}>
        <Typography variant="h6">{isEdit ? 'Edit User' : 'Create User'}</Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
      <Box component="form" onSubmit={handleSubmit} px={2} py={2} display="flex" flexDirection="column" gap={2}>
        <Typography variant="subtitle1" fontWeight="medium">
          User Details
        </Typography>
        
        <Input
          label="Full Name"
          name="name"
          placeholder="Enter full name"
          value={values.name}
          onChange={handleChange}
          fullWidth
          // required
          error={!!errors.name}
          helperText={errors.name}
          inputProps={{ maxLength: 50 }}
        />

        <Input
          label="Email"
          name="email"
          type="email"
          placeholder="Enter email address"
          value={values.email}
          onChange={handleChange}
          fullWidth
          // required
          error={!!errors.email}
          helperText={errors.email}
        />

        <Input
          label="Phone Number"
          name="phone_number"
          placeholder="Enter phone number (10 digits)"
          value={values.phone_number}
          onChange={handleChange}
          fullWidth
          error={!!errors.phone_number}
          helperText={errors.phone_number}
          inputProps={{ 
            maxLength: 10,
            pattern: "[0-9]*",
            inputMode: "numeric"
          }}
        />

        <Input
          label="Password"
          name="password"
          type="password"
          placeholder={isEdit ? 'Leave blank to keep current password' : 'Enter password'}
          value={values.password || ''}
          onChange={handleChange}
          fullWidth
          required={!isEdit}
          error={!!errors.password}
          helperText={errors.password}
        />

        <FormControlLabel
          control={
            <Switch
              name="is_active"
              checked={values.is_active}
              onChange={handleChange}
              color="primary"
            />
          }
          label="Active User"
        />

        <Box display="flex" gap={1} mt={1}>
          <Button type="submit" variant="primary">
            {isEdit ? 'Update' : 'Create'}
          </Button>
          <Button type="button" variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default UserFormDrawer;
